import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { axiosCaller } from '../../../utils/apiCaller';
import { getUserPermissionsAndResponsibilities } from '../../../actions/generalActions';
import MikaPageHeader from '../../../components/MikaPageHeader/MikaPageHeader';
import QuietHoursSection from './QuietHoursSection';
import EventTogglesSection from './EventTogglesSection';
import MutedSourcesSection from './MutedSourcesSection';
import RegisteredDevicesSection from './RegisteredDevicesSection';
import './SettingsPage.scss';

export interface EventType {
    id: number;
    key: string;
    module: string;
    label_key: string;
    description_key: string | null;
    default_priority: string;
}

export interface UserPreference {
    id: number;
    user_id: number;
    event_type_id: number | null;
    channel: string | null;
    enabled: boolean;
    digest_window: string | null;
    digest_time: string | null;
    quiet_hours_start: string | null;
    quiet_hours_end: string | null;
    timezone: string | null;
    dnd_until: string | null;
}

export interface MuteRow {
    id: number;
    user_id: number;
    // Backend column names — was previously misnamed source_type/source_id/expires_at
    // here, but the DB uses entity_type/entity_id/muted_until and the controller
    // returns rows verbatim. Aligning frontend with DB is cleaner than transforming
    // server-side: source_label is the human display name we denormalize at
    // mute-creation time (typically the rendered notification title).
    entity_type: string;
    entity_id: number;
    source_label: string | null;
    muted_until: string | null;
    created_at?: string;
}

export interface SubscriptionRow {
    id: number;
    platform: string;
    endpoint: string;
    device_label: string | null;
    user_agent: string | null;
    last_seen_at: string | null;
    created_at: string;
}

/**
 * Notification settings page. Four orthogonal control surfaces:
 *   1. Quiet hours — global suppression window for push channels.
 *   2. Event×channel toggles — what fires where, granular per event.
 *   3. Muted sources — entity-level mutes added from row kebab; managed here.
 *   4. Registered devices — push subscriptions; remove from any device.
 *
 * Data is fetched locally (not Redux) because this page is a leaf — nothing
 * else in the app reads notification preferences right now. If a future
 * surface needs them, we promote to Redux without breaking this page.
 */
const SettingsPage: React.FC = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const intl = useIntl();

    const [loading, setLoading] = useState(true);
    const [preferences, setPreferences] = useState<UserPreference[]>([]);
    const [eventTypes, setEventTypes] = useState<EventType[]>([]);
    const [mutes, setMutes] = useState<MuteRow[]>([]);
    const [subscriptions, setSubscriptions] = useState<SubscriptionRow[]>([]);

    // Same rehydration pattern every module page uses — without this, a hard
    // refresh on this route would strip the nav permissions.
    useEffect(() => {
        (dispatch as any)(getUserPermissionsAndResponsibilities());
    }, [dispatch]);

    const reloadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [prefsRes, mutesRes, subsRes] = await Promise.all([
                axiosCaller('GET', 'notifications/preferences') as any,
                axiosCaller('GET', 'notifications/mutes') as any,
                axiosCaller('GET', 'notifications/subscriptions') as any,
            ]);
            setPreferences(prefsRes?.data?.preferences || []);
            setEventTypes(prefsRes?.data?.event_types || []);
            setMutes(mutesRes?.data?.data || []);
            setSubscriptions(subsRes?.data?.data || []);
        } catch (e) {
            toast.error(intl.formatMessage({
                id: 'notifications.settings.loadError',
                defaultMessage: 'Could not load settings. Please try again.',
            }));
        } finally {
            setLoading(false);
        }
    }, [intl]);

    useEffect(() => {
        reloadAll();
    }, [reloadAll]);

    // Helpers used by sections — they mutate local state optimistically then
    // hit the API. On failure we re-pull to reconcile (cheap; settings page
    // isn't a hot path).
    const upsertPreference = useCallback(async (
        eventTypeId: number | null,
        channel: string | null,
        body: Partial<Pick<UserPreference, 'enabled' | 'quiet_hours_start' | 'quiet_hours_end' | 'timezone' | 'dnd_until'>>,
    ) => {
        try {
            const res: any = await axiosCaller('PUT', 'notifications/preferences', {
                event_type_id: eventTypeId,
                channel,
                ...body,
            });
            const updated: UserPreference = res?.data?.data;
            if (!updated) return;
            // Replace the matching row if present, else append.
            setPreferences((prev) => {
                const idx = prev.findIndex((p) =>
                    p.event_type_id === updated.event_type_id && p.channel === updated.channel,
                );
                if (idx === -1) return [...prev, updated];
                const next = prev.slice();
                next[idx] = updated;
                return next;
            });
        } catch (e) {
            toast.error(intl.formatMessage({
                id: 'notifications.settings.saveError',
                defaultMessage: 'Could not save. Please try again.',
            }));
            // Re-pull to undo any optimistic UI changes.
            reloadAll();
        }
    }, [intl, reloadAll]);

    const deleteMute = useCallback(async (muteId: number) => {
        // Optimistic
        const previousMutes = mutes;
        setMutes((prev) => prev.filter((m) => m.id !== muteId));
        try {
            // ID-based delete route (added 2026-05-22 alongside source_label migration).
            // Previously this called DELETE /mutes with {id: ...} but the legacy
            // route validates entity_type+entity_id and was 422-ing every unmute.
            await axiosCaller('DELETE', `notifications/mutes/${muteId}`);
        } catch (e) {
            setMutes(previousMutes);
            toast.error(intl.formatMessage({
                id: 'notifications.settings.saveError',
                defaultMessage: 'Could not save. Please try again.',
            }));
        }
    }, [mutes, intl]);

    const deleteSubscription = useCallback(async (subId: number) => {
        const previousSubs = subscriptions;
        setSubscriptions((prev) => prev.filter((s) => s.id !== subId));
        try {
            await axiosCaller('DELETE', `notifications/subscriptions/${subId}`);
        } catch (e) {
            setSubscriptions(previousSubs);
            toast.error(intl.formatMessage({
                id: 'notifications.settings.saveError',
                defaultMessage: 'Could not save. Please try again.',
            }));
        }
    }, [subscriptions, intl]);

    return (
        <div className="notif-settings">
            <MikaPageHeader
                title={intl.formatMessage({ id: 'notifications.settings.title', defaultMessage: 'Notification settings' })}
                subtitle={intl.formatMessage({ id: 'notifications.settings.subtitle', defaultMessage: 'Control what you get notified about, when, and where' })}
                onBack={() => history.push('/dashboard/notifications')}
            />

            <div className="notif-settings__body">
                {loading && (
                    <div className="notif-settings__loader" role="status" aria-live="polite">
                        <div className="notif-settings__loader-sonar" aria-hidden="true">
                            <span /><span /><span />
                            <div className="notif-settings__loader-core" />
                        </div>
                        <p className="notif-settings__loader-text">
                            <FormattedMessage id="notifications.archive.loading" defaultMessage="Loading…" />
                        </p>
                    </div>
                )}

                {!loading && (
                    <>
                        <QuietHoursSection
                            preferences={preferences}
                            onSave={upsertPreference}
                        />
                        <EventTogglesSection
                            eventTypes={eventTypes}
                            preferences={preferences}
                            onSave={upsertPreference}
                        />
                        <MutedSourcesSection
                            mutes={mutes}
                            eventTypes={eventTypes}
                            onUnmute={deleteMute}
                        />
                        <RegisteredDevicesSection
                            subscriptions={subscriptions}
                            onRemove={deleteSubscription}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default SettingsPage;
